import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection,
  deleteDoc,
  DocumentData,
  documentId,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  orderBy,
  query,
  QueryDocumentSnapshot,
  serverTimestamp,
  setDoc,
  startAfter,
  type Firestore,
  limit as queryLimit,
  writeBatch,
} from 'firebase/firestore';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { BOOTSTRAP_ADMIN_EMAILS, useAuth } from '../../lib/auth';
import { db, storage } from '../../lib/firebase';
import { AdminTabs } from './VoiceAdmin';

type AdminSource = 'bootstrap' | 'firestore' | null;
type SpeechCountSource = 'recordings' | 'profile';
type UserSortMode = 'speechesDesc' | 'newest' | 'lastOpened' | 'name';
type UsernameNormalizationStatus = 'ready' | 'invalid' | 'conflict';

type ParrotUser = {
  id: string;
  uid?: string;
  email?: string;
  name?: string;
  displayName?: string;
  username?: string;
  levelName?: string;
  totalSpeeches?: number;
  currentStreak?: number;
  longestStreak?: number;
  subscription?: unknown;
  subscriptionIsActive?: boolean;
  subscriptionType?: unknown;
  registrationEntryPoint?: string;
  timezone?: string;
  appVersion?: string;
  createdAt?: unknown;
  updatedAt?: unknown;
  lastOpened?: unknown;
  speechCount?: number;
  speechCountSource?: SpeechCountSource;
  adminVerified: boolean;
  adminSource: AdminSource;
};

type SpeechRecording = {
  id: string;
  topic: string;
  category?: string;
  recordedAt?: unknown;
  createdAt?: unknown;
  durationSec?: number;
  transcript?: string;
  overallScore?: number;
  wordCount?: number;
  audioURL?: string;
  audioStoragePath?: string;
  fileName?: string;
  raw: Record<string, unknown>;
};

type UsernameNormalizationCandidate = {
  userId: string;
  email?: string;
  currentUsername: string;
  normalizedUsername: string;
  status: UsernameNormalizationStatus;
  reasons: string[];
  conflict?: string;
  oldLookupDocId?: string;
};

type UsernameNormalizationPreview = {
  generatedAt: number;
  candidates: UsernameNormalizationCandidate[];
  readyCount: number;
  blockedCount: number;
  formatChangeCount: number;
  lookupRepairCount: number;
};

type SubscriptionSummary = {
  active: boolean;
  label: string;
  subline?: string;
  detail: string;
};

const PAGE_SIZE_OPTIONS = [50, 100, 200, 400, 500, 1000] as const;
type PageSizeOption = typeof PAGE_SIZE_OPTIONS[number];
type UserPageCursor = QueryDocumentSnapshot<unknown, DocumentData>;

const bootstrapAdminEmails = BOOTSTRAP_ADMIN_EMAILS.map((email) => email.toLowerCase());

export const UsersAdmin: React.FC = () => {
  const { user, signOutUser } = useAuth();
  const [users, setUsers] = useState<ParrotUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortMode, setSortMode] = useState<UserSortMode>('speechesDesc');
  const [pageSize, setPageSize] = useState<PageSizeOption>(50);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageCursors, setPageCursors] = useState<Array<UserPageCursor | null>>([null]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [totalUserCount, setTotalUserCount] = useState<number | null>(null);
  const [processingAdmin, setProcessingAdmin] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [selectedUser, setSelectedUser] = useState<ParrotUser | null>(null);
  const [selectedUserRecordings, setSelectedUserRecordings] = useState<SpeechRecording[]>([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [usernamePreview, setUsernamePreview] = useState<UsernameNormalizationPreview | null>(null);
  const [usernameDryRunLoading, setUsernameDryRunLoading] = useState(false);
  const [usernameApplyLoading, setUsernameApplyLoading] = useState(false);

  const loadUsersPage = useCallback(async (
    nextPageIndex = 0,
    nextPageSize: PageSizeOption = pageSize,
    nextPageCursors: Array<UserPageCursor | null> = pageCursors
  ) => {
    const database = db;
    if (!database) {
      setNotice({ type: 'error', text: 'Firebase is not configured.' });
      setLoading(false);
      return;
    }

    try {
      if (users.length === 0) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setNotice(null);

      const cursor = nextPageCursors[nextPageIndex] || null;
      const pageLimit = nextPageSize + 1;
      let snapshot: Awaited<ReturnType<typeof getDocs>>;
      try {
        const constraints = cursor
          ? [orderBy('createdAt', 'desc'), startAfter(cursor), queryLimit(pageLimit)]
          : [orderBy('createdAt', 'desc'), queryLimit(pageLimit)];
        snapshot = await getDocs(query(collection(database, 'users'), ...constraints));
      } catch (err) {
        // A few early Parrot users may not have createdAt. Fall back to
        // document-id ordering so the page still loads without a full
        // collection read.
        // eslint-disable-next-line no-console
        console.warn('[UsersAdmin] createdAt users read failed; falling back to document-id ordering', err);
        const fallbackConstraints = cursor
          ? [orderBy(documentId()), startAfter(cursor), queryLimit(pageLimit)]
          : [orderBy(documentId()), queryLimit(pageLimit)];
        snapshot = await getDocs(query(collection(database, 'users'), ...fallbackConstraints));
      }

      const docs = snapshot.docs.slice(0, nextPageSize);
      const nextCursor = docs[docs.length - 1] || null;
      const rows = docs.map((userDoc) => ({
        id: userDoc.id,
        ...(userDoc.data() as Record<string, unknown>),
        adminVerified: false,
        adminSource: null,
      })) as ParrotUser[];

      const hydrated = await Promise.all(
        rows.map(async (row): Promise<ParrotUser> => {
          const email = normalizeEmail(row.email);
          const isBootstrapAdmin = bootstrapAdminEmails.includes(email);
          const [hasAdminDoc, recordingCount] = await Promise.all([
            email ? checkAdminStatus(database, email) : Promise.resolve(false),
            countUserRecordings(database, row.id),
          ]);
          const adminSource: AdminSource = isBootstrapAdmin ? 'bootstrap' : hasAdminDoc ? 'firestore' : null;
          const profileSpeechCount = numberValue(row.totalSpeeches);
          const speechCount = Math.max(profileSpeechCount, recordingCount ?? 0);
          return {
            ...row,
            speechCount,
            speechCountSource: recordingCount === null ? 'profile' : 'recordings',
            adminVerified: isBootstrapAdmin || hasAdminDoc,
            adminSource,
          };
        })
      );

      setUsers(hydrated);
      setPageIndex(nextPageIndex);
      setHasNextPage(snapshot.docs.length > nextPageSize);
      setPageCursors((current) => {
        const base = nextPageCursors.length ? [...nextPageCursors] : [...current];
        base[nextPageIndex] = cursor;
        if (nextCursor) base[nextPageIndex + 1] = nextCursor;
        return base.slice(0, nextPageIndex + 2);
      });
      if (nextPageIndex === 0) {
        try {
          const countSnap = await getCountFromServer(collection(database, 'users'));
          setTotalUserCount(countSnap.data().count);
        } catch {
          setTotalUserCount(null);
        }
      }
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[UsersAdmin] Error loading users', err);
      setNotice({ type: 'error', text: err?.message || 'Error loading users.' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [pageCursors, pageSize, users.length]);

  useEffect(() => {
    loadUsersPage(0, pageSize, [null]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  const filteredUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const searched = !term ? users : users.filter((row) => {
      return [
        row.id,
        row.uid,
        row.email,
        row.name,
        row.displayName,
        row.username,
        row.levelName,
        subscriptionSummary(row).detail,
        row.registrationEntryPoint,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    });
    return [...searched].sort((a, b) => compareUsers(a, b, sortMode));
  }, [users, searchTerm, sortMode]);

  const stats = useMemo(() => {
    const admins = users.filter((row) => row.adminVerified).length;
    const speeches = users.reduce((sum, row) => sum + speechCountValue(row), 0);
    const activeLastWeek = users.filter((row) => {
      const ms = dateMs(row.lastOpened || row.updatedAt);
      return ms > 0 && Date.now() - ms <= 7 * 24 * 60 * 60 * 1000;
    }).length;
    return { admins, speeches, activeLastWeek };
  }, [users]);

  const pageStart = pageIndex * pageSize + 1;
  const pageEnd = pageIndex * pageSize + users.length;
  const totalPages = totalUserCount ? Math.max(1, Math.ceil(totalUserCount / pageSize)) : null;
  const visiblePageStart = users.length ? pageStart : 0;
  const visiblePageEnd = users.length ? pageEnd : 0;

  const refreshCurrentPage = () => {
    void loadUsersPage(pageIndex);
  };

  const goToPreviousPage = () => {
    if (pageIndex <= 0) return;
    void loadUsersPage(pageIndex - 1);
  };

  const goToNextPage = () => {
    if (!hasNextPage) return;
    void loadUsersPage(pageIndex + 1);
  };

  const changePageSize = (nextPageSize: PageSizeOption) => {
    setPageCursors([null]);
    setPageIndex(0);
    setUsers([]);
    setPageSize(nextPageSize);
  };

  const toggleAdminStatus = async (target: ParrotUser) => {
    const database = db;
    const email = normalizeEmail(target.email);
    const currentAdminEmail = normalizeEmail(user?.email);
    if (!database || !email) return;

    if (target.adminSource === 'bootstrap') {
      setNotice({ type: 'info', text: 'Bootstrap admins are managed in code so you cannot remove the owner account here.' });
      return;
    }

    try {
      setProcessingAdmin(email);
      setNotice(null);

      const adminRef = doc(database, 'admin', email);
      let successMessage: string;
      if (target.adminVerified) {
        await deleteDoc(adminRef);
        successMessage = `Removed admin access for ${email}.`;
      } else {
        await setDoc(adminRef, {
          email,
          displayEmail: target.email || email,
          uid: target.uid || target.id,
          grantedAt: serverTimestamp(),
          grantedBy: currentAdminEmail || 'unknown',
        }, { merge: true });
        successMessage = `Granted admin access to ${email}.`;
      }

      await loadUsersPage(pageIndex);
      setNotice({ type: 'success', text: successMessage });
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[UsersAdmin] Error toggling admin status', err);
      setNotice({ type: 'error', text: err?.message || 'Error toggling admin status.' });
    } finally {
      setProcessingAdmin(null);
    }
  };

  const openUserDetails = async (target: ParrotUser) => {
    const database = db;
    setSelectedUser(target);
    setSelectedUserRecordings([]);
    setDetailsError(null);
    setDetailsLoading(true);

    if (!database) {
      setDetailsError('Firebase is not configured.');
      setDetailsLoading(false);
      return;
    }

    try {
      const recordings = await loadUserRecordings(database, target.id);
      const updatedSpeechCount = Math.max(speechCountValue(target), recordings.length);
      const updatedUser: ParrotUser = {
        ...target,
        speechCount: updatedSpeechCount,
        speechCountSource: 'recordings',
      };
      setSelectedUser(updatedUser);
      setSelectedUserRecordings(recordings);
      setUsers((current) => current.map((row) => (
        row.id === target.id
          ? { ...row, speechCount: updatedSpeechCount, speechCountSource: 'recordings' as SpeechCountSource }
          : row
      )));
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[UsersAdmin] Error loading user details', err);
      setDetailsError(err?.message || 'Error loading user details.');
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeUserDetails = () => {
    setSelectedUser(null);
    setSelectedUserRecordings([]);
    setDetailsError(null);
    setDetailsLoading(false);
  };

  const runUsernameDryRun = async () => {
    const database = db;
    if (!database) {
      setNotice({ type: 'error', text: 'Firebase is not configured.' });
      return;
    }

    try {
      setUsernameDryRunLoading(true);
      setNotice(null);
      const preview = await buildUsernameNormalizationPreview(database, users);
      setUsernamePreview(preview);
      setNotice({
        type: 'info',
        text: `Dry run found ${preview.candidates.length} username issue${preview.candidates.length === 1 ? '' : 's'}: ${preview.readyCount} ready, ${preview.blockedCount} blocked.`,
      });
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[UsersAdmin] Error running username dry run', err);
      setNotice({ type: 'error', text: err?.message || 'Error running username dry run.' });
    } finally {
      setUsernameDryRunLoading(false);
    }
  };

  const applyUsernameNormalization = async () => {
    const database = db;
    const preview = usernamePreview;
    if (!database || !preview) return;

    const ready = preview.candidates.filter((candidate) => candidate.status === 'ready');
    if (!ready.length) {
      setNotice({ type: 'info', text: 'No safe username changes to apply. Run dry run again after resolving conflicts.' });
      return;
    }

    const confirmed = window.confirm(
      `Normalize ${ready.length} username${ready.length === 1 ? '' : 's'} now?\n\n` +
        'This updates users/{uid}.username and repairs usernames/{username} lookup docs.'
    );
    if (!confirmed) return;

    try {
      setUsernameApplyLoading(true);
      setNotice(null);
      const applied = await applyUsernameNormalizationPlan(database, ready, normalizeEmail(user?.email));
      setNotice({ type: 'success', text: `Normalized ${applied} username${applied === 1 ? '' : 's'} and refreshed lookup docs.` });
      setUsernamePreview(null);
      await loadUsersPage(pageIndex);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[UsersAdmin] Error applying username normalization', err);
      setNotice({ type: 'error', text: err?.message || 'Error applying username normalization.' });
    } finally {
      setUsernameApplyLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-sm text-neutral-500">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900">
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-lg font-bold">User Management</div>
            <div className="text-xs text-neutral-500">
              View Parrot users and grant admin access through Firestore admin docs.
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-neutral-500 font-mono">{user?.email}</span>
            <button
              onClick={signOutUser}
              className="text-xs text-neutral-600 underline hover:text-neutral-900"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        <AdminTabs current="users" />

        {notice && (
          <div className={`rounded-xl border px-4 py-3 text-sm ${noticeClassName(notice.type)}`}>
            {notice.text}
          </div>
        )}

        <section className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <StatCard label="Total Users" value={totalUserCount?.toLocaleString() || '...'} />
          <StatCard label="Loaded" value={users.length.toLocaleString()} />
          <StatCard label="Admins on page" value={stats.admins.toLocaleString()} />
          <StatCard label="Active 7d on page" value={stats.activeLastWeek.toLocaleString()} />
        </section>

        <UsernameNormalizationPanel
          preview={usernamePreview}
          dryRunLoading={usernameDryRunLoading}
          applyLoading={usernameApplyLoading}
          onDryRun={() => { void runUsernameDryRun(); }}
          onApply={() => { void applyUsernameNormalization(); }}
          onClear={() => setUsernamePreview(null)}
        />

        <section className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-neutral-200 flex flex-col md:flex-row md:items-center gap-3 justify-between">
            <div>
              <div className="font-semibold text-neutral-900">All app users</div>
              <div className="text-xs text-neutral-500">
                Showing {visiblePageStart.toLocaleString()}-{visiblePageEnd.toLocaleString()}
                {totalUserCount ? ` of ${totalUserCount.toLocaleString()}` : ''}. Search and sort apply to the loaded page.
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search users..."
                className="w-full sm:w-72 rounded-lg border border-neutral-200 px-3 py-2 text-sm outline-none focus:border-neutral-500"
              />
              <select
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value as UserSortMode)}
                className="rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm font-medium text-neutral-700 outline-none focus:border-neutral-500"
              >
                <option value="speechesDesc">Most speeches</option>
                <option value="newest">Newest users</option>
                <option value="lastOpened">Recently active</option>
                <option value="name">Name A-Z</option>
              </select>
              <select
                value={pageSize}
                onChange={(e) => changePageSize(Number(e.target.value) as PageSizeOption)}
                className="rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm font-medium text-neutral-700 outline-none focus:border-neutral-500"
              >
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option} / page
                  </option>
                ))}
              </select>
              <button
                onClick={refreshCurrentPage}
                disabled={refreshing}
                className="rounded-lg border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:border-neutral-400"
              >
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200 text-sm">
              <thead className="bg-neutral-50">
                <tr>
                  <Th>User</Th>
                  <Th>Level</Th>
                  <Th>Speeches</Th>
                  <Th>Streak</Th>
                  <Th>Joined</Th>
                  <Th>Last Opened</Th>
                  <Th>Subscription</Th>
                  <Th>Admin</Th>
                  <Th align="right">Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 bg-white">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-sm text-neutral-500">
                      No users match that search.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((row) => {
                    const email = normalizeEmail(row.email);
                    const processing = processingAdmin === email;
                    return (
                      <tr key={row.id} className="hover:bg-neutral-50/70">
                        <td className="px-4 py-4 align-top">
                          <div className="font-semibold text-neutral-900">
                            {displayName(row)}
                          </div>
                          <div className="text-xs text-neutral-500 font-mono">
                            {row.email || 'No email'}
                          </div>
                          <div className="text-xs text-neutral-400 font-mono mt-1">
                            {row.id}
                          </div>
                        </td>
                        <td className="px-4 py-4 align-top">
                          <div className="font-medium text-neutral-800">
                            {row.levelName || 'Unhatched Egg'}
                          </div>
                          <div className="text-xs text-neutral-500">
                            {row.registrationEntryPoint || 'parrot'}
                          </div>
                        </td>
                        <td className="px-4 py-4 align-top font-mono text-neutral-700">
                          {speechCountValue(row).toLocaleString()}
                        </td>
                        <td className="px-4 py-4 align-top">
                          <div className="font-mono text-neutral-700">{numberValue(row.currentStreak)}</div>
                          <div className="text-xs text-neutral-500">best {numberValue(row.longestStreak)}</div>
                        </td>
                        <td className="px-4 py-4 align-top text-neutral-600">
                          {formatDate(row.createdAt)}
                        </td>
                        <td className="px-4 py-4 align-top text-neutral-600">
                          {formatDate(row.lastOpened || row.updatedAt)}
                        </td>
                        <td className="px-4 py-4 align-top">
                          <SubscriptionBadge summary={subscriptionSummary(row)} />
                        </td>
                        <td className="px-4 py-4 align-top">
                          <AdminBadge source={row.adminSource} />
                        </td>
                        <td className="px-4 py-4 align-top">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => { void openUserDetails(row); }}
                              className="rounded-lg border border-neutral-200 bg-white px-3 py-2 text-xs font-semibold text-neutral-700 transition hover:border-neutral-400 hover:bg-neutral-50"
                            >
                              View details
                            </button>
                            <button
                              onClick={() => toggleAdminStatus(row)}
                              disabled={!email || processing || row.adminSource === 'bootstrap'}
                              className={`rounded-lg px-3 py-2 text-xs font-semibold border transition ${
                                row.adminVerified
                                  ? 'border-red-200 text-red-700 bg-red-50 hover:bg-red-100 disabled:hover:bg-red-50'
                                  : 'border-neutral-900 text-white bg-neutral-900 hover:bg-neutral-700'
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                              {processing
                                ? 'Saving...'
                                : row.adminSource === 'bootstrap'
                                  ? 'Bootstrap'
                                  : row.adminVerified
                                    ? 'Remove Admin'
                                    : 'Make Admin'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-3 border-t border-neutral-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-xs text-neutral-500">
              Page {pageIndex + 1}{totalPages ? ` of ${totalPages}` : ''} · {visiblePageStart.toLocaleString()}-{visiblePageEnd.toLocaleString()}
              {totalUserCount ? ` of ${totalUserCount.toLocaleString()} users` : ' users'}
            </div>
            <div className="flex gap-2">
              <button
                onClick={goToPreviousPage}
                disabled={pageIndex === 0 || refreshing}
                className="rounded-lg border border-neutral-200 bg-white px-3 py-2 text-xs font-semibold text-neutral-700 transition hover:border-neutral-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={goToNextPage}
                disabled={!hasNextPage || refreshing}
                className="rounded-lg border border-neutral-200 bg-white px-3 py-2 text-xs font-semibold text-neutral-700 transition hover:border-neutral-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </section>
      </main>

      {selectedUser && (
        <UserDetailsModal
          user={selectedUser}
          recordings={selectedUserRecordings}
          loading={detailsLoading}
          error={detailsError}
          onClose={closeUserDetails}
          onReload={() => { void openUserDetails(selectedUser); }}
        />
      )}
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="bg-white border border-neutral-200 rounded-xl p-4 shadow-sm">
    <div className="text-xs uppercase tracking-[0.18em] text-neutral-400 font-semibold">{label}</div>
    <div className="text-3xl font-bold text-neutral-900 mt-2">{value}</div>
  </div>
);

const Th: React.FC<{ children: React.ReactNode; align?: 'left' | 'right' }> = ({ children, align = 'left' }) => (
  <th className={`px-4 py-3 ${align === 'right' ? 'text-right' : 'text-left'} text-xs font-semibold uppercase tracking-wide text-neutral-500`}>
    {children}
  </th>
);

const AdminBadge: React.FC<{ source: AdminSource }> = ({ source }) => {
  if (source === 'bootstrap') {
    return <span className="inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 border border-blue-100">Bootstrap</span>;
  }
  if (source === 'firestore') {
    return <span className="inline-flex rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700 border border-green-100">Admin</span>;
  }
  return <span className="inline-flex rounded-full bg-neutral-100 px-3 py-1 text-xs font-semibold text-neutral-500 border border-neutral-200">User</span>;
};

const SubscriptionBadge: React.FC<{ summary: SubscriptionSummary }> = ({ summary }) => {
  const className = summary.active
    ? 'bg-green-50 text-green-700 border-green-100'
    : 'bg-neutral-100 text-neutral-500 border-neutral-200';
  return (
    <div className="flex flex-col items-start gap-1">
      <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${className}`}>
        {summary.label}
      </span>
      {summary.subline && (
        <span className="max-w-[8rem] text-xs text-neutral-500">
          {summary.subline}
        </span>
      )}
    </div>
  );
};

const UsernameNormalizationPanel: React.FC<{
  preview: UsernameNormalizationPreview | null;
  dryRunLoading: boolean;
  applyLoading: boolean;
  onDryRun: () => void;
  onApply: () => void;
  onClear: () => void;
}> = ({ preview, dryRunLoading, applyLoading, onDryRun, onApply, onClear }) => {
  const readyCount = preview?.readyCount ?? 0;
  const blockedCount = preview?.blockedCount ?? 0;
  const previewRows = preview?.candidates.slice(0, 10) ?? [];

  return (
    <section className="overflow-hidden rounded-xl border border-amber-200 bg-amber-50 shadow-sm">
      <div className="flex flex-col gap-4 p-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">Maintenance</div>
          <div className="mt-1 text-lg font-bold text-neutral-900">Normalize legacy usernames</div>
          <div className="mt-1 max-w-3xl text-sm text-amber-900/80">
            Dry run scans the loaded user page and <span className="font-mono">usernames</span>,
            flags malformed usernames like spaces/caps, and previews lookup table repairs before any database writes.
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={onDryRun}
            disabled={dryRunLoading || applyLoading}
            className="rounded-lg border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-900 transition hover:border-amber-500 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {dryRunLoading ? 'Scanning...' : 'Dry run'}
          </button>
          <button
            onClick={onApply}
            disabled={!readyCount || dryRunLoading || applyLoading}
            className="rounded-lg border border-neutral-900 bg-neutral-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-neutral-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {applyLoading ? 'Applying...' : `Apply ${readyCount || ''}`.trim()}
          </button>
          {preview && (
            <button
              onClick={onClear}
              disabled={dryRunLoading || applyLoading}
              className="rounded-lg border border-amber-200 bg-amber-100 px-4 py-2 text-sm font-semibold text-amber-900 transition hover:bg-amber-200 disabled:opacity-50"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {preview && (
        <div className="border-t border-amber-200 bg-white">
          <div className="grid grid-cols-2 gap-3 border-b border-neutral-100 p-4 text-sm md:grid-cols-5">
            <MiniStat label="Issues" value={preview.candidates.length.toLocaleString()} />
            <MiniStat label="Ready" value={readyCount.toLocaleString()} />
            <MiniStat label="Blocked" value={blockedCount.toLocaleString()} />
            <MiniStat label="Format changes" value={preview.formatChangeCount.toLocaleString()} />
            <MiniStat label="Lookup repairs" value={preview.lookupRepairCount.toLocaleString()} />
          </div>

          {preview.candidates.length === 0 ? (
            <div className="px-4 py-6 text-sm text-neutral-500">No malformed or missing username lookup records found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-100 text-sm">
                <thead className="bg-neutral-50">
                  <tr>
                    <Th>User</Th>
                    <Th>Current</Th>
                    <Th>Normalized</Th>
                    <Th>Status</Th>
                    <Th>Reason</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {previewRows.map((candidate) => (
                    <tr key={`${candidate.userId}:${candidate.currentUsername}`}>
                      <td className="px-4 py-3 align-top">
                        <div className="font-mono text-xs text-neutral-700">{candidate.userId}</div>
                        <div className="mt-1 text-xs text-neutral-500">{candidate.email || 'No email'}</div>
                      </td>
                      <td className="px-4 py-3 align-top font-mono text-neutral-700">{candidate.currentUsername}</td>
                      <td className="px-4 py-3 align-top font-mono text-neutral-900">{candidate.normalizedUsername || '-'}</td>
                      <td className="px-4 py-3 align-top">
                        <UsernameStatusBadge status={candidate.status} />
                      </td>
                      <td className="px-4 py-3 align-top text-neutral-600">
                        <div>{candidate.reasons.join(' · ')}</div>
                        {candidate.conflict && <div className="mt-1 text-xs text-red-700">{candidate.conflict}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.candidates.length > previewRows.length && (
                <div className="border-t border-neutral-100 px-4 py-3 text-xs text-neutral-500">
                  Showing first {previewRows.length}. Apply uses all {readyCount} ready rows.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

const MiniStat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-[0.14em] text-neutral-400 font-semibold">{label}</div>
    <div className="mt-1 text-xl font-bold text-neutral-900">{value}</div>
  </div>
);

const UsernameStatusBadge: React.FC<{ status: UsernameNormalizationStatus }> = ({ status }) => {
  if (status === 'ready') {
    return <span className="inline-flex rounded-full border border-green-100 bg-green-50 px-3 py-1 text-xs font-semibold text-green-700">Ready</span>;
  }
  if (status === 'conflict') {
    return <span className="inline-flex rounded-full border border-red-100 bg-red-50 px-3 py-1 text-xs font-semibold text-red-700">Conflict</span>;
  }
  return <span className="inline-flex rounded-full border border-amber-100 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">Invalid</span>;
};

const UserDetailsModal: React.FC<{
  user: ParrotUser;
  recordings: SpeechRecording[];
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onReload: () => void;
}> = ({ user, recordings, loading, error, onClose, onReload }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6">
    <div className="max-h-[92vh] w-full max-w-5xl overflow-hidden rounded-2xl bg-white shadow-2xl">
      <div className="flex items-start justify-between gap-4 border-b border-neutral-200 px-6 py-5">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-neutral-400">User details</div>
          <div className="mt-1 text-2xl font-bold text-neutral-900">{displayName(user)}</div>
          <div className="mt-1 font-mono text-xs text-neutral-500">{user.email || user.id}</div>
        </div>
        <button
          onClick={onClose}
          className="rounded-lg border border-neutral-200 px-3 py-2 text-sm font-semibold text-neutral-700 hover:border-neutral-400"
        >
          Close
        </button>
      </div>

      <div className="max-h-[calc(92vh-92px)] overflow-y-auto px-6 py-5">
        <section className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <StatCard label="Speeches" value={speechCountValue(user).toLocaleString()} />
          <StatCard label="Current Streak" value={numberValue(user.currentStreak).toLocaleString()} />
          <StatCard label="Best Streak" value={numberValue(user.longestStreak).toLocaleString()} />
          <StatCard label="Admin" value={user.adminVerified ? 'Yes' : 'No'} />
        </section>

        <section className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          <div className="rounded-xl border border-neutral-200 bg-white p-4">
            <div className="font-semibold text-neutral-900">Profile</div>
            <div className="mt-4 grid grid-cols-1 gap-3 text-sm">
              <InfoRow label="User ID" value={user.id} mono />
              <InfoRow label="UID" value={user.uid || user.id} mono />
              <InfoRow label="Name" value={displayName(user)} />
              <InfoRow label="Username" value={user.username || '-'} />
              <InfoRow label="Subscription" value={subscriptionSummary(user).detail} />
              <InfoRow label="Level" value={user.levelName || 'Unhatched Egg'} />
              <InfoRow label="Entry point" value={user.registrationEntryPoint || 'parrot'} />
              <InfoRow label="Timezone" value={user.timezone || '-'} />
              <InfoRow label="App version" value={user.appVersion || '-'} />
              <InfoRow label="Joined" value={formatDateTime(user.createdAt)} />
              <InfoRow label="Last opened" value={formatDateTime(user.lastOpened || user.updatedAt)} />
            </div>
          </div>

          <div className="rounded-xl border border-neutral-200 bg-neutral-950 p-4 text-neutral-50">
            <div className="flex items-center justify-between gap-3">
              <div className="font-semibold">Raw user info</div>
              <div className="text-xs text-neutral-400">Firestore + admin fields</div>
            </div>
            <pre className="mt-4 max-h-96 overflow-auto whitespace-pre-wrap rounded-lg bg-black/40 p-4 text-xs leading-relaxed text-neutral-200">
              {safeJson(user)}
            </pre>
          </div>
        </section>

        <section className="mt-6 rounded-xl border border-neutral-200 bg-white">
          <div className="flex flex-col gap-3 border-b border-neutral-200 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="font-semibold text-neutral-900">Speeches</div>
              <div className="text-xs text-neutral-500">
                Audio players appear when a recording has a remote audio URL.
              </div>
            </div>
            <button
              onClick={onReload}
              disabled={loading}
              className="rounded-lg border border-neutral-200 bg-white px-3 py-2 text-xs font-semibold text-neutral-700 hover:border-neutral-400 disabled:opacity-50"
            >
              {loading ? 'Loading...' : 'Refresh speeches'}
            </button>
          </div>

          {error && (
            <div className="m-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              {error}
            </div>
          )}

          {loading && !recordings.length ? (
            <div className="px-4 py-10 text-center text-sm text-neutral-500">Loading speech history...</div>
          ) : recordings.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-neutral-500">No speech recordings found.</div>
          ) : (
            <div className="divide-y divide-neutral-100">
              {recordings.map((recording) => (
                <RecordingDetailRow key={recording.id} recording={recording} />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  </div>
);

const InfoRow: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono = false }) => (
  <div>
    <div className="text-xs font-semibold uppercase tracking-wide text-neutral-400">{label}</div>
    <div className={`mt-1 break-words text-neutral-800 ${mono ? 'font-mono text-xs' : 'font-medium'}`}>{value}</div>
  </div>
);

const RecordingDetailRow: React.FC<{ recording: SpeechRecording }> = ({ recording }) => (
  <div className="p-4">
    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
      <div className="min-w-0">
        <div className="font-semibold text-neutral-900">{recording.topic}</div>
        <div className="mt-1 flex flex-wrap gap-2 text-xs text-neutral-500">
          <span>{formatDateTime(recording.recordedAt || recording.createdAt)}</span>
          <span>{formatDuration(recording.durationSec)}</span>
          {recording.category && <span>{recording.category}</span>}
          {recording.wordCount !== undefined && <span>{recording.wordCount.toLocaleString()} words</span>}
          {recording.fileName && <span className="font-mono">{recording.fileName}</span>}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {recording.overallScore !== undefined && (
          <span className="rounded-full bg-neutral-900 px-3 py-1 text-xs font-bold text-white">
            {recording.overallScore}/10
          </span>
        )}
        {recording.audioURL ? (
          <a
            href={recording.audioURL}
            target="_blank"
            rel="noreferrer"
            className="rounded-lg border border-neutral-200 px-3 py-2 text-xs font-semibold text-neutral-700 hover:border-neutral-400"
          >
            Open audio
          </a>
        ) : (
          <span className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs font-semibold text-neutral-400">
            Local-only audio
          </span>
        )}
      </div>
    </div>

    {recording.audioURL && (
      <audio controls preload="none" src={recording.audioURL} className="mt-3 w-full" />
    )}

    {recording.transcript && (
      <div className="mt-3 rounded-lg bg-neutral-50 p-3 text-sm leading-relaxed text-neutral-700">
        {recording.transcript}
      </div>
    )}
  </div>
);

async function buildUsernameNormalizationPreview(
  database: Firestore,
  rows: ParrotUser[]
): Promise<UsernameNormalizationPreview> {
  const lookupOwners = await loadUsernameLookupOwners(database);
  const usernameRows = rows
    .map((row) => {
      const raw = stringValue(row.username);
      if (!raw) return null;
      return {
        row,
        raw,
        normalized: normalizeUsername(raw),
      };
    })
    .filter((value): value is { row: ParrotUser; raw: string; normalized: string } => Boolean(value));

  const normalizedCounts = new Map<string, number>();
  for (const item of usernameRows) {
    if (!item.normalized) continue;
    normalizedCounts.set(item.normalized, (normalizedCounts.get(item.normalized) || 0) + 1);
  }

  const candidates: UsernameNormalizationCandidate[] = [];

  for (const item of usernameRows) {
    const userId = item.row.id;
    const rawTrimmed = item.raw.trim();
    const normalized = item.normalized;
    const validationError = validateNormalizedUsername(normalized);
    const lookupOwner = lookupOwners.get(normalized);
    const oldLookupOwner = lookupOwners.get(rawTrimmed);
    const lookupBelongsToUser = usernameLookupOwnerMatches(lookupOwner, item.row);
    const oldLookupBelongsToUser = usernameLookupOwnerMatches(oldLookupOwner, item.row);
    const needsFormatChange = rawTrimmed !== normalized;
    const lookupNeedsRepair = lookupOwner !== userId;
    const duplicateNormalized = normalized ? (normalizedCounts.get(normalized) || 0) > 1 : false;

    if (!needsFormatChange && !lookupNeedsRepair && !validationError) continue;

    const reasons: string[] = [];
    if (needsFormatChange) reasons.push('format change');
    if (!lookupOwner) reasons.push('lookup missing');
    if (lookupOwner && lookupOwner !== userId) reasons.push('lookup owner mismatch');
    if (validationError) reasons.push(validationError);
    if (duplicateNormalized) reasons.push('duplicate normalized username');

    let status: UsernameNormalizationStatus = 'ready';
    let conflict: string | undefined;
    if (validationError) {
      status = 'invalid';
    } else if (duplicateNormalized) {
      status = 'conflict';
      conflict = `Multiple user docs normalize to @${normalized}.`;
    } else if (lookupOwner && !lookupBelongsToUser) {
      status = 'conflict';
      conflict = `usernames/${normalized} is owned by ${lookupOwner}.`;
    }

    const oldLookupDocId = rawTrimmed !== normalized && !rawTrimmed.includes('/') && oldLookupBelongsToUser
      ? rawTrimmed
      : undefined;

    candidates.push({
      userId,
      email: item.row.email,
      currentUsername: item.raw,
      normalizedUsername: normalized,
      status,
      reasons,
      conflict,
      oldLookupDocId,
    });
  }

  const readyCount = candidates.filter((candidate) => candidate.status === 'ready').length;
  const blockedCount = candidates.length - readyCount;
  return {
    generatedAt: Date.now(),
    candidates,
    readyCount,
    blockedCount,
    formatChangeCount: candidates.filter((candidate) => candidate.currentUsername.trim() !== candidate.normalizedUsername).length,
    lookupRepairCount: candidates.filter((candidate) => candidate.reasons.some((reason) => reason.startsWith('lookup'))).length,
  };
}

async function applyUsernameNormalizationPlan(
  database: Firestore,
  candidates: UsernameNormalizationCandidate[],
  actorEmail: string
): Promise<number> {
  const ready = candidates.filter((candidate) => candidate.status === 'ready');
  const chunks = chunkArray(ready, 150);
  let applied = 0;

  for (const chunk of chunks) {
    const batch = writeBatch(database);
    for (const candidate of chunk) {
      const userRef = doc(database, 'users', candidate.userId);
      const usernameRef = doc(database, 'usernames', candidate.normalizedUsername);

      batch.set(userRef, {
        username: candidate.normalizedUsername,
        usernameLowercase: candidate.normalizedUsername,
        updatedAt: Date.now(),
        usernameNormalizedAt: serverTimestamp(),
        usernameNormalizedBy: actorEmail || 'admin',
      }, { merge: true });

      batch.set(usernameRef, {
        uid: candidate.userId,
        email: normalizeEmail(candidate.email),
        username: candidate.normalizedUsername,
        normalizedFrom: candidate.currentUsername,
        updatedAt: serverTimestamp(),
        normalizedAt: serverTimestamp(),
        normalizedBy: actorEmail || 'admin',
      }, { merge: true });

      if (candidate.oldLookupDocId) {
        batch.delete(doc(database, 'usernames', candidate.oldLookupDocId));
      }
    }

    await batch.commit();
    applied += chunk.length;
  }

  return applied;
}

async function loadUsernameLookupOwners(database: Firestore): Promise<Map<string, string>> {
  const snapshot = await getDocs(collection(database, 'usernames'));
  const owners = new Map<string, string>();
  snapshot.docs.forEach((lookupDoc) => {
    const data = lookupDoc.data() as Record<string, unknown>;
    owners.set(lookupDoc.id, stringValue(data.uid) || stringValue(data.userId) || '');
  });
  return owners;
}

async function checkAdminStatus(database: Firestore, email: string): Promise<boolean> {
  try {
    const adminDoc = await getDoc(doc(database, 'admin', email));
    return adminDoc.exists();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[UsersAdmin] Error checking admin status for', email, err);
    return false;
  }
}

async function countUserRecordings(database: Firestore, userId: string): Promise<number | null> {
  try {
    const countSnap = await getCountFromServer(collection(database, 'users', userId, 'recordings'));
    return countSnap.data().count;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[UsersAdmin] Error counting recordings for', userId, err);
    return null;
  }
}

async function loadUserRecordings(database: Firestore, userId: string): Promise<SpeechRecording[]> {
  let snapshot: Awaited<ReturnType<typeof getDocs>>;
  try {
    snapshot = await getDocs(query(collection(database, 'users', userId, 'recordings'), orderBy('createdAt', 'desc')));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[UsersAdmin] ordered recordings read failed; falling back to unordered read', err);
    snapshot = await getDocs(collection(database, 'users', userId, 'recordings'));
  }

  const recordings = await Promise.all(
    snapshot.docs.map((recordingDoc) => normalizeRecording(recordingDoc.id, recordingDoc.data() as Record<string, unknown>))
  );
  return recordings.sort((a, b) => dateMs(b.recordedAt || b.createdAt) - dateMs(a.recordedAt || a.createdAt));
}

async function normalizeRecording(id: string, data: Record<string, unknown>): Promise<SpeechRecording> {
  const topic = topicText(data.topic)
    || stringValue(data.topicText)
    || stringValue(data.prompt)
    || stringValue(data.question)
    || stringValue(data.title)
    || stringValue(data.text)
    || 'Untitled speech';

  const audioStoragePath = stringValue(data.storagePath)
    || stringValue(data.audioStoragePath)
    || stringValue(data.firebaseStoragePath);

  return {
    id,
    topic,
    category: stringValue(data.category) || topicCategory(data.topic),
    recordedAt: data.recordedAt,
    createdAt: data.createdAt,
    durationSec: finiteNumber(data.durationSec) ?? finiteNumber(data.duration) ?? finiteNumber(data.seconds),
    transcript: stringValue(data.transcript) || stringValue(data.transcription) || stringValue(data.textToSpeech),
    overallScore: recordingScore(data),
    wordCount: finiteNumber(data.wordCount),
    audioURL: await resolveRecordingAudioURL(data, audioStoragePath),
    audioStoragePath,
    fileName: stringValue(data.fileName) || stringValue(data.filename) || stringValue(data.audioFileName),
    raw: data,
  };
}

async function resolveRecordingAudioURL(data: Record<string, unknown>, storagePath?: string): Promise<string | undefined> {
  const remote = stringValue(data.firebaseURL)
    || stringValue(data.audioURL)
    || stringValue(data.audioUrl)
    || stringValue(data.remoteAudioURL)
    || stringValue(data.remoteAudioUrl)
    || stringValue(data.downloadURL)
    || stringValue(data.downloadUrl);
  if (remote) return remote;

  if (!storage || !storagePath) return undefined;
  try {
    return await getDownloadURL(storageRef(storage, storagePath));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[UsersAdmin] Error resolving recording storage URL', storagePath, err);
    return undefined;
  }
}

function displayName(row: ParrotUser): string {
  return row.name || row.displayName || row.username || row.email || 'Unnamed user';
}

function subscriptionSummary(row: ParrotUser): SubscriptionSummary {
  const subscription = asRecord(row.subscription);
  const subscriptionType = asRecord(row.subscriptionType);
  const entitlement = stringValue(subscription?.entitlement)
    || stringValue(subscription?.entitlementId)
    || stringValue(subscription?.entitlementID);
  const productId = stringValue(subscription?.productId)
    || stringValue(subscription?.productID)
    || stringValue(subscription?.productIdentifier)
    || stringValue(subscriptionType?.productId)
    || stringValue(subscriptionType?.name)
    || stringValue(subscriptionType?.type)
    || stringValue(row.subscriptionType);
  const status = stringValue(subscription?.status)?.toLowerCase();
  const explicitActive = booleanValue(subscription?.isActive)
    ?? booleanValue(row.subscriptionIsActive)
    ?? booleanValue(subscriptionType?.grantsParrotAccess)
    ?? booleanValue(subscriptionType?.isActive)
    ?? (status ? ['active', 'trialing', 'trial', 'pro', 'plus'].includes(status) : undefined);
  const expiry = asDate(
    subscription?.renewsAt
      || subscription?.renewalDate
      || subscription?.expiresAt
      || subscription?.expirationDate
      || subscription?.trialEndsAt
  );

  const active = explicitActive ?? (expiry ? expiry.getTime() > Date.now() : false);
  const plan = planLabel(productId || entitlement);
  const label = active ? (plan ? `Pro · ${plan}` : 'Pro') : 'Free';
  let subline: string | undefined;
  if (active && expiry) {
    subline = `${subscription?.trialEndsAt ? 'Trial ends' : 'Renews'} ${formatDate(expiry)}`;
  } else if (!active && expiry) {
    subline = `Expired ${formatDate(expiry)}`;
  } else if (!active && status && status !== 'inactive') {
    subline = status;
  }

  return {
    active,
    label,
    subline,
    detail: [label, subline].filter(Boolean).join(' · '),
  };
}

function planLabel(value?: string): string | undefined {
  if (!value) return undefined;
  const normalized = value.toLowerCase();
  if (normalized.includes('annual') || normalized.includes('year')) return 'Annual';
  if (normalized.includes('monthly') || normalized.includes('month')) return 'Monthly';
  if (normalized.includes('weekly') || normalized.includes('week')) return 'Weekly';
  if (normalized === 'plus') return 'Plus';
  return value;
}

function normalizeEmail(email?: string | null): string {
  return (email || '').trim().toLowerCase();
}

function normalizeUsername(username: string): string {
  const trimmed = username.trim().replace(/^@+/, '').toLowerCase();
  return trimmed.replace(/[^a-z0-9_.-]/g, '');
}

function validateNormalizedUsername(username: string): string | null {
  if (!username) return 'empty after normalization';
  if (username.length < 4) return 'less than 4 characters';
  if (username.length > 20) return 'more than 20 characters';
  if (!/^[a-z0-9][a-z0-9_.-]*[a-z0-9]$/.test(username)) {
    return 'must start and end with a letter or number';
  }
  return null;
}

function usernameLookupOwnerMatches(owner: string | undefined, row: ParrotUser): boolean {
  if (!owner) return false;
  return owner === row.id || (!!row.uid && owner === row.uid);
}

function numberValue(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function finiteNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function booleanValue(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', 'yes', '1', 'active'].includes(normalized)) return true;
    if (['false', 'no', '0', 'inactive'].includes(normalized)) return false;
  }
  return undefined;
}

function speechCountValue(row: ParrotUser): number {
  return Math.max(numberValue(row.speechCount), numberValue(row.totalSpeeches));
}

function compareUsers(a: ParrotUser, b: ParrotUser, sortMode: UserSortMode): number {
  switch (sortMode) {
    case 'speechesDesc':
      return speechCountValue(b) - speechCountValue(a) || dateMs(b.lastOpened || b.updatedAt || b.createdAt) - dateMs(a.lastOpened || a.updatedAt || a.createdAt);
    case 'lastOpened':
      return dateMs(b.lastOpened || b.updatedAt) - dateMs(a.lastOpened || a.updatedAt) || speechCountValue(b) - speechCountValue(a);
    case 'name':
      return displayName(a).localeCompare(displayName(b)) || speechCountValue(b) - speechCountValue(a);
    case 'newest':
    default:
      return dateMs(b.createdAt) - dateMs(a.createdAt) || speechCountValue(b) - speechCountValue(a);
  }
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function chunkArray<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
}

function topicText(value: unknown): string | undefined {
  const direct = stringValue(value);
  if (direct) return direct;
  const record = asRecord(value);
  return stringValue(record?.text)
    || stringValue(record?.title)
    || stringValue(record?.prompt);
}

function topicCategory(value: unknown): string | undefined {
  return stringValue(asRecord(value)?.category);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function recordingScore(data: Record<string, unknown>): number | undefined {
  const scores = asRecord(data.scores);
  const subScores = asRecord(data.subScores);
  return finiteNumber(data.overallScore)
    ?? finiteNumber(data.blendedOverall)
    ?? finiteNumber(data.overall)
    ?? finiteNumber(data.score)
    ?? finiteNumber(scores?.overall)
    ?? finiteNumber(subScores?.overall);
}

function dateMs(value: unknown): number {
  const date = asDate(value);
  return date ? date.getTime() : 0;
}

function formatDate(value: unknown): string {
  const date = asDate(value);
  if (!date) return '-';
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTime(value: unknown): string {
  const date = asDate(value);
  if (!date) return '-';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDuration(seconds?: number): string {
  if (seconds === undefined || !Number.isFinite(seconds)) return '-';
  const rounded = Math.max(0, Math.round(seconds));
  const mins = Math.floor(rounded / 60);
  const secs = rounded % 60;
  if (mins <= 0) return `${secs}s`;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

function asDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string') {
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? null : new Date(ms);
  }
  if (typeof value === 'object') {
    const maybeTimestamp = value as { toDate?: () => Date; seconds?: number };
    if (typeof maybeTimestamp.toDate === 'function') return maybeTimestamp.toDate();
    if (typeof maybeTimestamp.seconds === 'number') return new Date(maybeTimestamp.seconds * 1000);
  }
  return null;
}

function safeJson(value: unknown): string {
  try {
    return JSON.stringify(value, (_key, nestedValue) => {
      if (nestedValue && typeof nestedValue === 'object') {
        const maybeTimestamp = nestedValue as { toDate?: () => Date; seconds?: number };
        if (typeof maybeTimestamp.toDate === 'function') return maybeTimestamp.toDate().toISOString();
        if (typeof maybeTimestamp.seconds === 'number') return new Date(maybeTimestamp.seconds * 1000).toISOString();
      }
      return nestedValue;
    }, 2);
  } catch (_err) {
    return String(value);
  }
}

function noticeClassName(type: 'success' | 'error' | 'info'): string {
  switch (type) {
    case 'success':
      return 'bg-green-50 text-green-800 border-green-200';
    case 'error':
      return 'bg-red-50 text-red-800 border-red-200';
    case 'info':
      return 'bg-blue-50 text-blue-800 border-blue-200';
  }
}
